import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Edit2, Trash2, Save, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import Card, { CardContent } from '../ui/Card';
import { supabase } from '../../lib/supabase';

interface ClientNote {
  id: string;
  note: string;
  created_at: string;
  updated_at: string;
  created_by_email?: string;
}

interface ClientNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}

export const ClientNotesPanel: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchNotes();
    }
  }, [clientId]);

  const fetchNotes = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_notes')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsSaving(true);
    try {
      // Get current user email
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || 'usuario desconocido';

      const { data, error } = await supabase
        .from('client_notes')
        .insert({
          client_id: clientId,
          note: newNote.trim(),
          created_by_email: userEmail,
        })
        .select()
        .single();

      if (error) throw error;

      setNotes(prev => [data, ...prev]);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditNote = (noteId: string, currentText: string) => {
    setEditingNote(noteId);
    setEditingText(currentText);
  };

  const handleSaveEdit = async (noteId: string) => {
    if (!editingText.trim()) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('client_notes')
        .update({
          note: editingText.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev =>
        prev.map(note =>
          note.id === noteId
            ? { ...note, note: editingText.trim(), updated_at: new Date().toISOString() }
            : note
        )
      );

      setEditingNote(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta nota?')) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('client_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingNote(null);
    setEditingText('');
  };

  return (
    <Card className="!p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Notas
          <span className="ml-1.5 text-xs font-normal text-gray-400">{notes.length}</span>
        </h3>
      </div>
      <CardContent>
        <div className="flex gap-2 mb-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Nueva nota…"
            className="flex-1 px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm resize-none"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddNote();
              }
            }}
          />
          <Button
            onClick={handleAddNote}
            disabled={!newNote.trim() || isSaving}
            size="sm"
            className="!px-2 !py-1 self-start"
            icon={isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          >
            Agregar
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">Sin notas.</p>
        ) : (
          <div className="max-h-36 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {notes.map((note) => (
              <div key={note.id} className="py-1.5">
                {editingNote === note.id ? (
                  <div className="space-y-1.5">
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm resize-none"
                      rows={2}
                    />
                    <div className="flex gap-1.5">
                      <Button
                        onClick={() => handleSaveEdit(note.id)}
                        disabled={!editingText.trim() || isSaving}
                        size="sm"
                        className="!px-2 !py-0.5 !text-[11px]"
                        icon={isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      >
                        Guardar
                      </Button>
                      <Button onClick={cancelEdit} variant="outline" size="sm" className="!px-2 !py-0.5 !text-[11px]">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 group">
                    <p className="flex-1 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap leading-snug">
                      {note.note}
                    </p>
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleEditNote(note.id, note.note)}
                        className="p-1 text-gray-400 hover:text-gray-700"
                        title="Editar"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="shrink-0 text-[10px] text-gray-400 pt-0.5" title={note.created_by_email || ''}>
                      {new Date(note.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}
                      {note.updated_at !== note.created_at ? ' · editada' : ''}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ClientNotesModal: React.FC<ClientNotesModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            Notas de {clientName}
          </Dialog.Title>
          <ClientNotesPanel clientId={clientId} />
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ClientNotesModal;
